import { useCallback, useMemo } from 'react';
import {
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  fetchHomeMemberships,
  deriveQuickActionsFromMemberships,
  fetchHomeSessionDeck,
  fetchHomeClubsSnapshot,
  fetchHomeClubsWithLeaderboardTeasers,
  fetchHomeActivity,
  fetchAppClubsCount,
  fetchHomeLastSessionSnapshot,
} from '../services/homeDashboardService';
import { fetchPlayerActivityFeed } from '../services/activityFeedService';
import { fetchPlayerTopMatchups } from '../services/matchupService';
import { HOMEPAGE_MODE, deriveHomepageMode } from '../utils/homePageMode';

const keyId = (id) => id ?? '__none__';

export const homeQueryKeys = {
  root: ['home'],
  memberships: (userId) => ['home', 'memberships', keyId(userId)],
  sessionDeck: (userId, siteRole) => ['home', 'sessionDeck', keyId(userId), siteRole ?? 'member'],
  clubsPreview: (userId) => ['home', 'clubsPreview', keyId(userId)],
  clubLeaderboardTeasers: (userId) => ['home', 'clubLeaderboardTeasers', keyId(userId)],
  activity: (userId) => ['home', 'activity', keyId(userId)],
  momentsFeed: (userId) => ['home', 'momentsFeed', keyId(userId)],
  matchups: (userId) => ['home', 'matchups', keyId(userId)],
  lastSessionSnapshot: (userId) => ['home', 'lastSessionSnapshot', keyId(userId)],
  appClubsTotal: () => ['home', 'appClubsTotal'],
};

const EMPTY_QUICK = {
  canCreateSession: false,
  clubsForSessionCreate: [],
};

const EMPTY_SNAPSHOT = { clubs: [] };

/**
 * Home dashboard: parallel TanStack queries, stale-while-revalidate, granular loading.
 */
export function useHomeDashboardData(currentUser) {
  const queryClient = useQueryClient();
  const userId = currentUser?.id;
  const siteRole = currentUser?.role || 'member';
  const enabled = !!userId;

  const membershipsQuery = useQuery({
    queryKey: homeQueryKeys.memberships(userId),
    queryFn: () => fetchHomeMemberships(userId),
    enabled,
    staleTime: 60_000,
    gcTime: 6 * 60_000,
  });

  const sessionDeckQuery = useQuery({
    queryKey: homeQueryKeys.sessionDeck(userId, siteRole),
    enabled,
    staleTime: 30_000,
    gcTime: 6 * 60_000,
    queryFn: async () => {
      const memberships = await queryClient.ensureQueryData({
        queryKey: homeQueryKeys.memberships(userId),
        queryFn: () => fetchHomeMemberships(userId),
      });
      return fetchHomeSessionDeck(userId, siteRole, memberships);
    },
  });

  const clubsPreviewQuery = useQuery({
    queryKey: homeQueryKeys.clubsPreview(userId),
    enabled,
    staleTime: 60_000,
    gcTime: 6 * 60_000,
    queryFn: async () => {
      const memberships = await queryClient.ensureQueryData({
        queryKey: homeQueryKeys.memberships(userId),
        queryFn: () => fetchHomeMemberships(userId),
      });
      return fetchHomeClubsSnapshot(memberships, userId);
    },
  });

  const clubLeaderboardTeasersQuery = useQuery({
    queryKey: homeQueryKeys.clubLeaderboardTeasers(userId),
    enabled,
    staleTime: 45_000,
    gcTime: 6 * 60_000,
    queryFn: async () => {
      const memberships = await queryClient.ensureQueryData({
        queryKey: homeQueryKeys.memberships(userId),
        queryFn: () => fetchHomeMemberships(userId),
      });
      const baseSnapshot = await queryClient.ensureQueryData({
        queryKey: homeQueryKeys.clubsPreview(userId),
        queryFn: () => fetchHomeClubsSnapshot(memberships, userId),
      });
      return fetchHomeClubsWithLeaderboardTeasers(
        baseSnapshot,
        userId,
        currentUser?.gender ?? null
      );
    },
  });

  const activityQuery = useQuery({
    queryKey: homeQueryKeys.activity(userId),
    queryFn: () => fetchHomeActivity(userId),
    enabled,
    staleTime: 45_000,
    gcTime: 6 * 60_000,
  });

  const momentsFeedQuery = useQuery({
    queryKey: homeQueryKeys.momentsFeed(userId),
    queryFn: () => fetchPlayerActivityFeed({ limit: 10 }),
    enabled,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });

  const matchupsQuery = useQuery({
    queryKey: homeQueryKeys.matchups(userId),
    queryFn: () => fetchPlayerTopMatchups({ limit: 5 }),
    enabled,
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
  });

  const lastSessionSnapshotQuery = useQuery({
    queryKey: homeQueryKeys.lastSessionSnapshot(userId),
    queryFn: () => fetchHomeLastSessionSnapshot(userId, currentUser?.gender ?? null),
    enabled,
    staleTime: 45_000,
    gcTime: 6 * 60_000,
  });

  const appClubsTotalQuery = useQuery({
    queryKey: homeQueryKeys.appClubsTotal(),
    queryFn: fetchAppClubsCount,
    enabled,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: homeQueryKeys.root });
  }, [queryClient]);

  const quickActionsFlags = membershipsQuery.data
    ? deriveQuickActionsFromMemberships(membershipsQuery.data)
    : EMPTY_QUICK;

  const nextGame = sessionDeckQuery.data?.nextGame ?? null;
  const discoverySessions = sessionDeckQuery.data?.discoverySessions ?? [];
  const happeningSoon = sessionDeckQuery.data?.happeningSoon ?? [];
  const upcomingInTwoWeeks = sessionDeckQuery.data?.upcomingInTwoWeeks ?? [];
  const membershipCount = membershipsQuery.data?.length ?? 0;

  const homepageMode = useMemo(() => {
    const sessionDeckStillBlank =
      enabled &&
      sessionDeckQuery.isPending &&
      sessionDeckQuery.data === undefined;
    if (sessionDeckStillBlank) {
      return HOMEPAGE_MODE.ACTIVE;
    }
    return deriveHomepageMode({
      hasPersonalNextGame: Boolean(nextGame),
      membershipCount,
    });
  }, [
    enabled,
    sessionDeckQuery.isPending,
    sessionDeckQuery.data,
    nextGame,
    membershipCount,
  ]);

  /** Full membership strip for flows that need every club (e.g. home stats picker), not capped like {@link clubsSnapshot}. */
  const performanceClubs = useMemo(() => {
    const rows = membershipsQuery.data || [];
    return rows.map((m) => ({
      id: m.club_id,
      name: m.clubs?.name || 'Unknown Club',
      location: m.clubs?.location ?? null,
      club_role: m.club_role || 'member',
    }));
  }, [membershipsQuery.data]);

  const memberClubIds = useMemo(
    () => new Set((membershipsQuery.data || []).map((m) => m.club_id)),
    [membershipsQuery.data]
  );

  // Use enriched snapshot (with leaderboard teasers) when available; fall back to base.
  const clubsSnapshot =
    clubLeaderboardTeasersQuery.data ??
    clubsPreviewQuery.data ??
    EMPTY_SNAPSHOT;
  const activityItems = activityQuery.data ?? [];
  const momentsFeedItems = momentsFeedQuery.data ?? [];
  const matchupsItems = matchupsQuery.data ?? [];
  const appClubsTotal =
    appClubsTotalQuery.error != null ? null : (appClubsTotalQuery.data ?? null);

  return {
    /** Resolved club memberships count (quick actions subtitles, mode). */
    membershipCount,

    // Back-compat combined flags (prefer granular below for UI)
    loading: enabled && membershipsQuery.isPending,
    error:
      membershipsQuery.error?.message ??
      null,

    /** Per-section UX */
    membershipsPending: enabled && membershipsQuery.isPending,
    sessionDeckPending: enabled && sessionDeckQuery.isPending,
    // Show skeleton until both base clubs and leaderboard teasers are ready.
    clubsPending: enabled && (clubsPreviewQuery.isPending || clubLeaderboardTeasersQuery.isPending),
    activityPending: enabled && activityQuery.isPending,
    momentsFeedPending: enabled && momentsFeedQuery.isPending,
    matchupsPending: enabled && matchupsQuery.isPending,
    lastSessionPending: enabled && lastSessionSnapshotQuery.isPending,

    sessionDeckError: sessionDeckQuery.error?.message ?? null,
    clubsError: clubsPreviewQuery.error?.message ?? null,
    activityError: activityQuery.error?.message ?? null,
    momentsFeedError: momentsFeedQuery.error?.message ?? null,
    matchupsError: matchupsQuery.error?.message ?? null,
    lastSessionError: lastSessionSnapshotQuery.error?.message ?? null,

    nextGame,
    happeningSoon,
    discoverySessions,
    upcomingInTwoWeeks,
    homepageMode,
    clubsSnapshot,
    activityItems,
    momentsFeedItems,
    matchupsItems,
    lastSessionSnapshot: lastSessionSnapshotQuery.data ?? null,
    quickActionsFlags,
    performanceClubs,
    memberClubIds,
    appClubsTotal,
    refresh,
  };
}
